import type { CharacterState, Rules, SpellAllocation } from "../data/types";
import { rulesRegistry } from "../data/rules";
import { calcDerivedStats } from "./characterCalc";
import { checkUltimateEligibility } from "./ultimateWarlock";

export interface UpgradeNotice {
  title: string;
  detail: string;
}

export interface OverCapSpell {
  spellId: string;
  listId: string;
  points: number;
}

export interface RulesUpgradePlan {
  fromRules: Rules;
  toRules: Rules;
  toVersion: string;
  headlines: string[]; // general rule changes, regardless of character
  notices: UpgradeNotice[]; // changes affecting this character, applied automatically
  maxSpellLevel: number | null; // per-spell cap under the new rules for this character
  overCapSpells: OverCapSpell[]; // manual: points above the cap must be redistributed
  // manual: indices into state.standaloneGifts of mission rewards with no
  // Patron listed; a Patron (or none) can be assigned so they count towards
  // holding a Gift from each Patron.
  untiedMissionGifts: number[];
}

// Next version + general headline changes for each upgradable rules version.
const UPGRADES: Record<string, { to: string; headlines: string[] }> = {
  "1": {
    to: "2",
    headlines: [
      "War Magic spells now have levels: each extra point allows another cast within a minute for no extra MP. War Magic durations are halved.",
      "All spells have a max level of 8 (Ultimate Warlocks are exempt). Points above level 8 may be redistributed to other spells in the same group.",
      "HP and MP are capped at 8, or 13 for an Ultimate Warlock.",
      "Ultimate Warlocks must currently hold a Gift from each of the Thirteen, and gain 13 HP, 13 MP and Royalty.",
      "The Judge and the Witness join the Thirteen, replacing the Tinker and the Trickster, who become Previous Patrons.",
      "Resist costs 2 MP and is Instant; Heal is Instant; Mage Armour lasts 1 day but can only be cast once per Short Rest; Stun lasts 5 seconds.",
      "Ill-Gotten Gains now steals pegs from NPCs (and Ghosts can't be looted).",
      "Royalty (Wealth Level 5) now grants 5 gold plus 5 silver every dawn, and a fancy title of your choice.",
      "Bureau of Eldritch Administration favours the Judge and the Sycophant; the Alchemy Guild favours the Undying and the Witness. Custom Cabals need a Patron's endorsement.",
    ],
  },
};

export function planRulesUpgrade(
  state: CharacterState
): RulesUpgradePlan | null {
  const upgrade = UPGRADES[state.rulesVersion];
  const fromRules = rulesRegistry[state.rulesVersion];
  const toRules = upgrade && rulesRegistry[upgrade.to];
  if (!upgrade || !fromRules || !toRules) return null;

  const notices: UpgradeNotice[] = [];
  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];

  // Derived stat changes (caps, Ultimate Warlock)
  const before = calcDerivedStats(state, fromRules);
  const after = calcDerivedStats(state, toRules);
  if (state.ultimateWarlock) {
    const missing = checkUltimateEligibility(state, toRules)
      .groups.flatMap((g) => g.items)
      .filter((i) => !i.met)
      .map((i) => i.label);
    notices.push({
      title: "Ultimate Warlock",
      detail:
        `Your ascension carries over. ${toRules.metadata.label} Ultimate Warlocks have ${toRules.limits.ultimateWarlock?.hp} HP, ${toRules.limits.ultimateWarlock?.mp} MP, Royalty, and no spell level cap.` +
        (missing.length > 0
          ? ` (Under ${toRules.metadata.label} rules you would not yet qualify; still missing: ${missing.join("; ")}.)`
          : ""),
    });
  }
  const statChanges = [
    { label: "Max HP", from: before.maxHp, to: after.maxHp },
    { label: "MP", from: before.mp, to: after.mp },
    {
      label: "Wealth",
      from: fromRules.wealthLevels[before.wealthLevel]?.name,
      to: toRules.wealthLevels[after.wealthLevel]?.name,
    },
  ].filter((c) => c.from !== c.to);
  for (const c of statChanges) {
    notices.push({ title: c.label, detail: `${c.from} → ${c.to}` });
  }

  // Patrons the character has dealt with whose standing changed
  const patronIds = new Set([
    ...allPacts.map((p) => p.patronId),
    ...(state.standaloneGifts || []).map((g) => g.patronId),
    ...(state.resurrectionPacts || []).map((r) => r.patronId),
  ]);
  for (const id of patronIds) {
    if (!id) continue;
    const oldPatron = fromRules.patrons.find((p) => p.id === id);
    const newPatron = toRules.patrons.find((p) => p.id === id);
    if (!newPatron) {
      notices.push({
        title: oldPatron?.name ?? id,
        detail: "This Patron no longer exists. Speak to a GM.",
      });
    } else if (newPatron.exiled && !oldPatron?.exiled) {
      notices.push({
        title: newPatron.name,
        detail:
          "Now a Previous Patron: your pacts and Gifts remain, and its Challenges still award MP, but it no longer counts towards the Challenge of 13 or Ultimate Warlock.",
      });
    }
  }

  // Core skills held (directly or via Gift of Earthly Experience) whose text changed
  const skillIds = new Set([
    ...state.coreSkillSelections.map((s) => s.skillId),
    ...allPacts.map((p) => p.giftSkillId),
    ...(state.standaloneGifts || []).map((g) => g.giftSkillId),
  ]);
  for (const id of skillIds) {
    const oldSkill = fromRules.coreSkills.find((s) => s.id === id);
    const newSkill = toRules.coreSkills.find((s) => s.id === id);
    if (oldSkill && newSkill && oldSkill.description !== newSkill.description) {
      notices.push({ title: newSkill.name, detail: newSkill.description });
    }
  }

  // Gifts held whose text changed
  const giftIds = new Set([
    ...allPacts.map((p) => p.giftId),
    ...(state.standaloneGifts || []).map((g) => g.giftId),
  ]);
  for (const id of giftIds) {
    const oldGift = fromRules.otherworldlyGifts.find((g) => g.id === id);
    const newGift = toRules.otherworldlyGifts.find((g) => g.id === id);
    if (oldGift && newGift && oldGift.description !== newGift.description) {
      notices.push({ title: newGift.name, detail: newGift.description });
    }
  }

  // Known spells whose duration, effect or scaling changed
  const oldSpells = fromRules.spellLists.flatMap((l) => l.spells);
  const newSpells = toRules.spellLists.flatMap((l) => l.spells);
  for (const alloc of state.spellAllocations) {
    if (alloc.points <= 0) continue;
    const oldSpell = oldSpells.find((s) => s.id === alloc.spellId);
    const newSpell = newSpells.find((s) => s.id === alloc.spellId);
    if (!oldSpell || !newSpell || newSpell.deprecated) continue;
    const parts: string[] = [];
    if (oldSpell.duration !== newSpell.duration)
      parts.push(`Duration ${oldSpell.duration} → ${newSpell.duration}.`);
    if (oldSpell.scaling === "binary" && newSpell.scaling !== "binary")
      parts.push(
        "Can now be levelled up with extra points for repeat casts within a minute."
      );
    if (oldSpell.effect !== newSpell.effect) parts.push(newSpell.effect);
    if (parts.length > 0)
      notices.push({ title: newSpell.name, detail: parts.join(" ") });
  }

  // Spells above the new level cap (Ultimate Warlocks are exempt)
  const cap = after.maxSpellLevel;
  const overCapSpells: OverCapSpell[] =
    cap === null
      ? []
      : state.spellAllocations.flatMap((alloc) => {
          const spell = newSpells.find((s) => s.id === alloc.spellId);
          return spell && alloc.points > cap
            ? [
                {
                  spellId: alloc.spellId,
                  listId: spell.listId,
                  points: alloc.points,
                },
              ]
            : [];
        });

  return {
    fromRules,
    toRules,
    toVersion: upgrade.to,
    headlines: upgrade.headlines,
    notices,
    maxSpellLevel: cap,
    overCapSpells,
    untiedMissionGifts: (state.standaloneGifts || []).flatMap((g, i) =>
      g.source !== "exchange" && !g.patronId ? [i] : []
    ),
  };
}

export function applyRulesUpgrade(
  state: CharacterState,
  plan: RulesUpgradePlan,
  choices: {
    spellAllocations: SpellAllocation[];
    missionGiftPatrons: Record<number, string>; // standaloneGifts index → patron id
  }
): CharacterState {
  return {
    ...state,
    rulesVersion: plan.toVersion,
    spellAllocations: choices.spellAllocations.filter((a) => a.points > 0),
    standaloneGifts: state.standaloneGifts?.map((g, i) =>
      choices.missionGiftPatrons[i]
        ? { ...g, patronId: choices.missionGiftPatrons[i] }
        : g
    ),
  };
}
